import { useState } from "react";
import { Page } from "../components/AppShell";
import { BarList, Heatmap } from "../components/charts";
import {
  Card,
  PanelState,
  RangeFilter,
  StatTile,
} from "../components/ui";
import { num, pct, titleCase } from "../lib/format";
import { useApi } from "../lib/useApi";
import type { AudienceResponse } from "../lib/types";

/** The trade — a repeat channel — as opposed to a homeowner, a single sale. */
const TRADE = new Set([
  "PAINTER",
  "CONTRACTOR",
  "DEALER",
  "RETAILER",
  "INTERIOR_DESIGNER",
  "ARCHITECT",
]);

export function AudiencePage() {
  const [days, setDays] = useState(90);
  const { data, error, loading, stale } = useApi<AudienceResponse>(
    "/api/admin/analytics/audience",
    { days },
  );

  const totalUsers = data?.profiles.reduce((n, p) => n + p.total, 0) ?? 0;
  const trade =
    data?.profiles
      .filter((p) => TRADE.has(p.profileType))
      .reduce((n, p) => n + p.total, 0) ?? 0;

  const totalProjects = (data?.uploads ?? 0) + (data?.fromTemplates ?? 0);

  return (
    <Page
      title="Audience"
      subtitle="Who they are, where they are, and when they paint"
      actions={<RangeFilter days={days} onChange={setDays} />}
    >
      <div className={stale ? "stale" : undefined}>
        <PanelState loading={loading} error={error}>
          {data && (
            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              <div className="grid grid-kpi">
                <StatTile
                  label="Trade accounts"
                  value={trade}
                  hint={
                    totalUsers > 0
                      ? `${pct((trade / totalUsers) * 100, 0)} of all customers`
                      : undefined
                  }
                />
                <StatTile
                  label="Home owners"
                  value={
                    data.profiles.find((p) => p.profileType === "HOME_OWNER")
                      ?.total ?? 0
                  }
                  hint="single-sale audience"
                />
                <StatTile
                  label="Pincodes reached"
                  value={data.distinctPincodes}
                  hint="distinct, all time"
                />
                <StatTile
                  label="Own photos"
                  value={
                    totalProjects > 0
                      ? pct((data.uploads / totalProjects) * 100, 0)
                      : "—"
                  }
                  hint={`${num(data.uploads)} of ${num(totalProjects)} rooms`}
                />
              </div>

              <Card
                title="When people paint"
                note="Renders by local weekday and hour. This is the panel a campaign gets scheduled against — hover any cell for the exact count."
              >
                <PanelState
                  loading={false}
                  error={null}
                  empty={data.heatmap.peak === 0}
                  emptyText="No renders in this period."
                >
                  <Heatmap
                    grid={data.heatmap.grid}
                    peak={data.heatmap.peak}
                    timezone={data.heatmap.timezone}
                  />
                </PanelState>
              </Card>

              <div className="grid grid-2">
                <Card
                  title="Who they are"
                  note="A painter is a repeat channel; a home owner is one sale. This split is what changes the campaign, not the headcount."
                >
                  <PanelState
                    loading={false}
                    error={null}
                    empty={data.profiles.length === 0}
                  >
                    <BarList
                      items={data.profiles.map((p) => ({
                        key: p.profileType,
                        label: titleCase(p.profileType),
                        value: p.total,
                        meta: p.recent > 0 ? `+${p.recent} new` : undefined,
                      }))}
                    />
                  </PanelState>
                </Card>

                <Card
                  title="What they paint"
                  note="Rooms started in this window, by type."
                >
                  <PanelState
                    loading={false}
                    error={null}
                    empty={data.rooms.length === 0}
                  >
                    <BarList
                      items={data.rooms.map((r) => ({
                        key: r.roomType,
                        label: titleCase(r.roomType),
                        value: r.projects,
                      }))}
                    />
                  </PanelState>
                </Card>

                <Card
                  title="Where they are"
                  note="Top pincodes by customer count — where a dealer push would land on people who already use the app."
                  aside={
                    data.truncated
                      ? `sample of ${num(data.scanned)}`
                      : `${num(data.scanned)} customers`
                  }
                >
                  <PanelState
                    loading={false}
                    error={null}
                    empty={data.pincodes.length === 0}
                    emptyText="No pincodes on file yet."
                  >
                    <BarList
                      items={data.pincodes.map((p) => ({
                        key: p.pincode,
                        label: <span className="mono">{p.pincode}</span>,
                        value: p.total,
                        meta: p.recent > 0 ? `+${p.recent} new` : undefined,
                      }))}
                    />
                  </PanelState>
                </Card>

                <Card
                  title="Sample rooms"
                  note="Which pre-shot rooms get used. The cheapest content decision in the product — a template nobody opens is a slot for one that does."
                >
                  <PanelState
                    loading={false}
                    error={null}
                    empty={data.templates.length === 0}
                    emptyText="Nobody started from a sample room in this period."
                  >
                    <BarList
                      items={data.templates.map((t) => ({
                        key: t.id,
                        label: t.name,
                        value: t.projects,
                        meta: t.roomType ? titleCase(t.roomType) : undefined,
                      }))}
                    />
                  </PanelState>
                </Card>
              </div>
            </div>
          )}
        </PanelState>
      </div>
    </Page>
  );
}
